import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { formatISO } from 'date-fns';
import { Company, Installer } from '~gc/domains/users';
import { DetailedWorkOrder } from '~gc/domains/work/models/detailed-work-order';
import { WorkOrder } from '~gc/domains/work/work-order/work-order.model';
import { WorkOrderDate } from '../../../core/calendar';
import { awardedInstallerProfileShown } from '../../../domains/users/users-ui.actions';
import { MayHaveInstallerTeamLeadId } from '../../pipes/is-awarded-to-installer.pipe';
import { CustomDateForm, CustomDateFormArray } from '../schedule/schedule.form';
import { AuthFacade } from '~gc/domains/app/auth/auth.facade';
import { isRole } from '~gc/domains/app/auth/auth.maps';
import { catchError, of, take } from 'rxjs';
import { ModalController } from '@ionic/angular';
import {
  CompanyInsightsService,
  WorkOrderResponseSummary,
} from '../../../domains/company-insights/company-insights.service';
import { WorkOrderViewersModalComponent } from './work-order-viewers-modal.component';

@Component({
  selector: 'gc-detail-header',
  templateUrl: './detail-header.component.html',
  styleUrls: ['./detail-header.component.scss'],
})
export class DetailHeaderComponent implements OnChanges, OnInit {
  @Input() workOrder!: DetailedWorkOrder;
  @Input() workOrderDates: WorkOrderDate[] = [];
  @Input() matchedInstallerCount!: number;
  @Input() currentRole!: string;
  @Input() currentUser!: any & MayHaveInstallerTeamLeadId;
  @Input() allowedPrivateDetails!: boolean;
  @Input() scheduleOpen = true;
  @Input() isChatOpen!: boolean;
  @Input() company!: Company;

  @Output() viewInstaller = new EventEmitter<Installer>();
  @Output() reviewInstallers = new EventEmitter<WorkOrder>();
  @Output() dateAdded = new EventEmitter<WorkOrderDate>();
  @Output() dateSaved = new EventEmitter<WorkOrderDate>();
  @Output() dateDeleted = new EventEmitter<WorkOrderDate>();

  datesForm!: CustomDateFormArray;

  /** TT-260 item 2: how many opened the work and how many declined, for the company. */
  responseSummary?: WorkOrderResponseSummary;
  private summaryFor?: string;

  constructor(
    public readonly auth: AuthFacade,
    private readonly insights: CompanyInsightsService,
    private readonly modals: ModalController,
  ) {}

  /** Paul, Sprint Review 10/14: who is behind the "N viewed" count. */
  async showViewers(): Promise<void> {
    if (!this.responseSummary?.seen || !this.workOrder?.id) return;
    const modal = await this.modals.create({
      component: WorkOrderViewersModalComponent,
      componentProps: { workOrderId: this.workOrder.id },
    });
    await modal.present();
  }

  ngOnInit() {
    this.datesForm = new CustomDateFormArray(this.workOrderDates.map(date => new CustomDateForm(date)) || [], () => ({
      workOrderId: this.workOrder.id,
    }));
    this.loadResponseSummary();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['workOrderDates'] && !changes['workOrderDates'].firstChange) {
      this.datesForm.repopulate(changes['workOrderDates'].currentValue || []);
    }
    if ((changes['workOrder'] && !changes['workOrder'].firstChange) || changes['currentRole']) {
      this.loadResponseSummary();
    }
  }

  private loadResponseSummary(): void {
    const isCompany = isRole(this.currentRole, 'companyAdmin', 'companyManager');
    const id = this.workOrder?.id;
    if (!isCompany || !id || this.summaryFor === id) return;
    this.summaryFor = id;
    this.insights
      .responseSummary(id)
      .pipe(
        take(1),
        catchError(() => of(undefined)),
      )
      .subscribe(summary => (this.responseSummary = summary));
  }

  addDate(date: WorkOrderDate): void {
    this.dateAdded.emit({
      ...date,
      type: 'workorder',
      startDate: typeof date.startDate === 'string' ? date.startDate : formatISO(date.startDate as unknown as Date),
      endDate: typeof date.endDate === 'string' ? date.endDate : formatISO(date.endDate as unknown as Date),
      workOrder: this.workOrder,
    });
  }

  saveDate(date: WorkOrderDate): void {
    this.dateSaved.emit({
      ...date,
      type: 'workorder',
      startDate: typeof date.startDate === 'string' ? date.startDate : formatISO(date.startDate as unknown as Date),
      endDate: typeof date.endDate === 'string' ? date.endDate : formatISO(date.endDate as unknown as Date),
      workOrder: this.workOrder,
    });
  }

  deleteDate(date: WorkOrderDate): void {
    this.dateDeleted.emit({
      ...date,
      type: 'workorder',
      workOrder: this.workOrder,
    });
  }
}
